/**
 * Centralized Demo Mode Composable
 * 
 * This composable provides a single source of truth for demo mode detection
 * across the entire application. It uses the ProjectContextManager which
 * resolves demo mode very early in the application lifecycle.
 * 
 * This replaces multiple scattered demo mode checks throughout the app
 * and provides consistent, reactive demo mode state.
 */

import { computed } from 'vue';
import { useProjectContext } from '@/services/project-context-manager';

export function useCentralizedDemo() {
  const projectContext = useProjectContext();

  // Reactive demo mode state from the centralized project context
  const demoMode = projectContext.demoMode;

  return {
    // Primary demo mode checks (reactive)
    isDemoProject: computed(() => demoMode.value.isDemoProject),
    isDemoDomain: computed(() => demoMode.value.isDemoDomain), 
    isDemoMode: computed(() => demoMode.value.isDemoMode),

    // Reactive computed values (explicit)
    isDemoProjectReactive: computed(() => demoMode.value.isDemoProject),
    isDemoDomainReactive: computed(() => demoMode.value.isDemoDomain),
    isDemoModeReactive: computed(() => demoMode.value.isDemoMode),

    // Legacy compatibility (matching existing useDemo interface)
    isDemo: computed(() => demoMode.value.isDemoProject),
    shouldBypassAuthentication: computed(() => demoMode.value.isDemoMode),
    shouldLoadDemoData: computed(() => demoMode.value.isDemoProject),

    // Debug info
    getDemoModeDebugInfo: () => ({
      isDemoProject: demoMode.value.isDemoProject,
      isDemoDomain: demoMode.value.isDemoDomain,
      isDemoMode: demoMode.value.isDemoMode,
      source: 'ProjectContextManager',
      timestamp: new Date().toISOString()
    })
  };
}

// Note: Don't export a singleton instance here as it would cause initialization issues
// Components should call useCentralizedDemo() directly when needed
